import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Rectangle,
    ResponsiveContainer,
    XAxis,
    YAxis
} from 'recharts';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiService from '../../api/apiService';
import { updateHazardFilters } from '../../store/correcttrackSlice';
import { useRefetchTrigger } from '../../hooks/useRefetchTrigger';
import AuthUtil from '../Utils/AuthUtil';
import OrgApiService from '../../api/OrgApiService';

const RISK_LEVELS = [
    { key: 'Low', color: '#99C20E' },
    { key: 'Medium', color: '#f4c430' },
    { key: 'High', color: '#f28c28' },
    { key: 'Very High', color: '#df3f3f' },
];
const RISK_LEVEL_KEYS = {
    Low: 'riskLevel.low',
    Medium: 'riskLevel.medium',
    High: 'riskLevel.high',
    'Very High': 'riskLevel.veryHigh',
};
const MAX_TOOLTIP_RISKS = 3;
const TOOLTIP_OFFSET_X = 16;
const TOOLTIP_OFFSET_Y = 16;
const GROUP_BY = {
    DEPARTMENT: 'department',
    LOCATION: 'location',
};


const ControlledRiskPortalTooltip = ({ departmentData, activeRiskLevel, mousePos }) => {
    const { t } = useTranslation();
    if (!activeRiskLevel || !departmentData) return null;

    const risks = departmentData.risksByLevel?.[activeRiskLevel] || [];
    if (risks.length === 0) return null;

    const displayedRisks = risks.slice(0, MAX_TOOLTIP_RISKS);
    const extraRisks = risks.length - displayedRisks.length;
    const localizedLevel = t(RISK_LEVEL_KEYS[activeRiskLevel] ?? activeRiskLevel);

    return ReactDOM.createPortal(
        <Box
            sx={{
                position: 'fixed',
                top: mousePos.y + TOOLTIP_OFFSET_Y,
                left: mousePos.x + TOOLTIP_OFFSET_X,
                maxWidth: 320,
                borderRadius: '14px',
                border: '1px solid rgba(31, 42, 55, 0.12)',
                backgroundColor: '#ffffff',
                boxShadow: '0 18px 44px rgba(15, 23, 42, 0.2)',
                p: 1.5,
                zIndex: 9999,
                pointerEvents: 'none',
            }}
        >
            <Typography sx={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#71808c', fontWeight: 800 }}>
                {departmentData.department}
            </Typography>
            <Typography sx={{ fontSize: 15, color: '#1d2a32', fontWeight: 900, mb: 1 }}>
                {t('dashboardRiskByDept.tooltip.summary', { level: localizedLevel, count: risks.length })}
            </Typography>
            <Stack spacing={0.75}>
                {displayedRisks.map((risk, index) => (
                    <Box
                        key={`${risk.id}-${index}`}
                        sx={{
                            borderRadius: '9px',
                            backgroundColor: '#f1f5f9',
                            px: 1,
                            py: 0.75
                        }}
                    >
                        <Typography sx={{ fontSize: 12, color: '#26343d', fontWeight: 700, lineHeight: 1.25 }}>
                            {risk.name}
                        </Typography>
                    </Box>
                ))}
            </Stack>
            {extraRisks > 0 && (
                <Typography sx={{ mt: 1, fontSize: 12, color: '#60707c', fontWeight: 700 }}>
                    {t('dashboardRiskByDept.tooltip.more', { count: extraRisks })}
                </Typography>
            )}
            <Typography sx={{ mt: 1, fontSize: 11, color: '#99C20E', fontWeight: 700, letterSpacing: '0.04em' }}>
                {t('dashboardRiskByDept.tooltip.click')}
            </Typography>
        </Box>,
        document.body
    );
};

const getBarShape = (levelKey, activeDepartment, activeRiskLevel) => {
    return (props) => {
        const { x, y, width, height, fill, payload } = props;

        if (!width || !height) {
            return null;
        }

        const isActive = activeDepartment === payload.department && activeRiskLevel === levelKey;

        return (
            <Rectangle
                x={isActive ? x - 2 : x}
                y={isActive ? y - 3 : y}
                width={isActive ? width + 4 : width}
                height={isActive ? height + 6 : height}
                fill={fill}
                radius={levelKey === 'Very High' ? [0, 10, 10, 0] : [5, 5, 5, 5]}
                style={{
                    filter: isActive ? 'drop-shadow(0 8px 12px rgba(15, 23, 42, 0.28))' : 'none',
                    transition: 'none'
                }}
            />
        );
    };
};

const DashboardRiskByDepartment = ({ jsonSettings }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const refetchTrigger = useRefetchTrigger();
    const userToken = useSelector((state) => state.correcttrack.userToken);
    const isSuperAdmin = useMemo(() => userToken && AuthUtil.isSuperAdmin(userToken), [userToken]);
    const orgApiService = useMemo(() => new OrgApiService(), []);

    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeRiskLevel, setActiveRiskLevel] = useState(null);
    const [activeDepartmentData, setActiveDepartmentData] = useState(null);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const [currentOrgName, setCurrentOrgName] = useState('');
    const settings = useMemo(() => {
        try {
            return jsonSettings ? JSON.parse(jsonSettings) : {};
        }
        catch {
            return {};
        }
    }, [jsonSettings]);
    const configuredGroupBy = settings.groupBy === GROUP_BY.LOCATION ? GROUP_BY.LOCATION : GROUP_BY.DEPARTMENT;
    const groupBy = configuredGroupBy;
    const groupLabel = groupBy === GROUP_BY.LOCATION
        ? t('dashboardRiskByDept.group.location')
        : t('dashboardRiskByDept.group.department');
    const title = groupBy === GROUP_BY.LOCATION
        ? t('dashboardRiskByDept.title.location')
        : t('dashboardRiskByDept.title.department');

    const loadCurrentOrgName = async () => {
        if (!isSuperAdmin || currentOrgName) {
            return currentOrgName;
        }

        const userId = AuthUtil.getUserId(userToken);
        const response = await orgApiService.getUsersOrg(userId);
        const orgName = response?.data?.ogName || '';
        setCurrentOrgName(orgName);
        return orgName;
    };

    const handleBarClick = async (groupName, riskLevel) => {
        if (!groupName || !riskLevel) return;
        const items = [
            {
                id: 1,
                columnField: groupBy === GROUP_BY.LOCATION ? 'hzProject' : 'hzDepartmentName',
                operatorValue: 'equals',
                value: groupName
            },
            { id: 2, columnField: 'hzRisKLevelControlled', operatorValue: 'equals', value: riskLevel },
        ];
        const orgName = await loadCurrentOrgName();

        if (isSuperAdmin && orgName) {
            items.push({ id: 3, columnField: 'hzOrganizationName', operatorValue: 'equals', value: orgName });
        }

        dispatch(updateHazardFilters({
            items,
            linkOperator: 'and',
        }));
        navigate('/Hazards');
    };

    useEffect(() => {
        let ignore = false;

        async function loadControlledRiskData() {
            setLoading(true);
            setError('');

            try {
                const response = await apiService.getData(`api/Hazard/riskByDepartment/forOrg?groupBy=${encodeURIComponent(groupBy)}`);

                if (ignore) {
                    return;
                }

                setChartData(response?.data?.data || []);
            }
            catch (loadError) {
                console.error('Error loading risk by department data:', loadError);
                setError(t('dashboardRiskByDept.error.load'));
            }
            finally {
                if (!ignore) {
                    setLoading(false);
                }
            }
        }

        void loadControlledRiskData();

        return () => {
            ignore = true;
        };
    }, [t, refetchTrigger, groupBy]);

    useEffect(() => {
        if (!isSuperAdmin) {
            setCurrentOrgName('');
            return;
        }

        void loadCurrentOrgName();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuperAdmin, userToken]);

    const summaryMetrics = useMemo(() => {
        return {
            totalRisks: chartData.reduce((total, department) => total + department.total, 0),
            totalVeryHigh: chartData.reduce((total, department) => total + department['Very High'], 0),
            totalGroups: chartData.length
        };
    }, [chartData]);

    const updateMousePos = (event) => {
        setMousePos({ x: event.clientX, y: event.clientY });
    };

    return (
        <Box
            onMouseMove={updateMousePos}
            sx={{
                width: '100%',
                height: '100%',
                overflow: 'visible',
                position: 'relative',
                zIndex: 1,
                borderRadius: 0,
                background: 'transparent',
                p: 2.25,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5
            }}
        >
            <Stack direction='row' justifyContent='space-between' alignItems='flex-start' spacing={2}>
                <Box>
                    <Typography sx={{ fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#6f7a86', fontWeight: 700 }}>
                        {t('dashboardRiskByDept.kicker')}
                    </Typography>
                    <Typography variant='h6' sx={{ fontWeight: 800, lineHeight: 1.15, color: '#1d2a32' }}>
                        {title}
                    </Typography>
                </Box>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        borderRadius: '12px',
                        border: '1px solid rgba(31, 42, 55, 0.1)',
                        background: 'rgba(255, 255, 255, 0.76)',
                        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)',
                        p: 1.25
                    }}
                >
                    <Typography sx={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#71808c', fontWeight: 800 }}>
                        {t('dashboardRiskByDept.totalRisks')}
                    </Typography>
                    <Typography sx={{ fontSize: 24, lineHeight: 1, color: '#1d2a32', fontWeight: 900 }}>
                        {summaryMetrics.totalRisks}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        borderRadius: '12px',
                        border: '1px solid rgba(223, 63, 63, 0.22)',
                        background: 'linear-gradient(135deg, rgba(223, 63, 63, 0.12), rgba(255, 255, 255, 0.8))',
                        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)',
                        p: 1.25
                    }}
                >
                    <Typography sx={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8a5555', fontWeight: 800 }}>
                        {t('dashboardRiskByDept.veryHigh')}
                    </Typography>
                    <Typography sx={{ fontSize: 24, lineHeight: 1, color: '#9f2424', fontWeight: 900 }}>
                        {summaryMetrics.totalVeryHigh}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        borderRadius: '12px',
                        border: '1px solid rgba(153, 194, 14, 0.26)',
                        background: 'linear-gradient(135deg, rgba(153, 194, 14, 0.14), rgba(255, 255, 255, 0.8))',
                        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)',
                        p: 1.25
                    }}
                >
                    <Typography sx={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#66751f', fontWeight: 800 }}>
                        {groupLabel}
                    </Typography>
                    <Typography sx={{ fontSize: 24, lineHeight: 1, color: '#405000', fontWeight: 900 }}>
                        {summaryMetrics.totalGroups}
                    </Typography>
                </Box>
            </Stack>

            <Box sx={{ flex: 1, minHeight: 0, overflow: 'visible' }}>
                {loading ? (
                    <Stack alignItems='center' justifyContent='center' sx={{ height: '100%' }}>
                        <CircularProgress size={30} sx={{ color: '#99C20E' }} />
                    </Stack>
                ) : error ? (
                    <Stack alignItems='center' justifyContent='center' sx={{ height: '100%' }}>
                        <Typography variant='body1' color='error'>
                            {error}
                        </Typography>
                    </Stack>
                ) : chartData.length === 0 ? (
                    <Stack alignItems='center' justifyContent='center' sx={{ height: '100%' }}>
                        <Typography variant='body1' color='text.secondary'>
                            {t('dashboardRiskByDept.empty.noRisks')}
                        </Typography>
                    </Stack>
                ) : (
                    <ResponsiveContainer width='100%' height='100%'>
                        <BarChart
                            data={chartData}
                            layout='vertical'
                            margin={{ top: 8, right: 20, left: 18, bottom: 8 }}
                        >
                            <CartesianGrid strokeDasharray='3 3' horizontal={false} stroke='rgba(31, 42, 55, 0.12)' />
                            <XAxis
                                type='number'
                                allowDecimals={false}
                                tick={{ fontSize: 11, fill: '#52616b' }}
                            />
                            <YAxis
                                type='category'
                                dataKey='department'
                                width={110}
                                tick={{ fontSize: 11, fill: '#52616b', fontWeight: 600 }}
                            />
                            {RISK_LEVELS.map((level) => (
                                <Bar
                                    key={level.key}
                                    dataKey={level.key}
                                    stackId='controlledRisk'
                                    fill={level.color}
                                    radius={level.key === 'Very High' ? [0, 8, 8, 0] : [0, 0, 0, 0]}
                                    shape={getBarShape(level.key, activeDepartmentData?.department, activeRiskLevel)}
                                    cursor='pointer'
                                    onMouseEnter={(data) => {
                                        setActiveRiskLevel(level.key);
                                        setActiveDepartmentData(data?.payload ?? null);
                                    }}
                                    onMouseLeave={() => {
                                        setActiveRiskLevel(null);
                                        setActiveDepartmentData(null);
                                    }}
                                    onClick={(data) => handleBarClick(data?.payload?.department, level.key)}
                                />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </Box>
            <ControlledRiskPortalTooltip
                departmentData={activeDepartmentData}
                activeRiskLevel={activeRiskLevel}
                mousePos={mousePos}
            />
        </Box>
    );
};

export default DashboardRiskByDepartment;
